import { useState } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { useNavigate, Link } from "react-router-dom";
import { api, formatRupiah } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { Loader2, LogOut, KeyRound, User as UserIcon, Briefcase, Wallet, Percent, Phone, FileText, ChevronRight, Sparkles } from "lucide-react";

export default function EmployeeProfile() {
  const { user, logout } = useAuth();
  const nav = useNavigate();

  const handleLogout = () => {
    logout();
    nav("/login");
  };

  const isHourly = user?.pay_type === "hourly";

  return (
    <div className="px-4 py-6 max-w-md mx-auto space-y-5">
      <div className="flex items-start justify-between">
        <div>
          <p className="overline mb-1">Profil</p>
          <h1 className="font-display text-2xl font-bold tracking-tight">{user?.name}</h1>
          <p className="text-sm text-zinc-500">{user?.employee_id}</p>
        </div>
        <Button variant="outline" size="sm" onClick={handleLogout} data-testid="btn-emp-logout">
          <LogOut className="w-4 h-4 mr-1.5" /> Keluar
        </Button>
      </div>

      <Card className="p-4 border border-zinc-200 shadow-none">
        <p className="overline mb-3">Informasi Pegawai</p>
        <div className="divide-y divide-zinc-100">
          <InfoRow icon={UserIcon} label="Nama Lengkap" value={user?.name} />
          <InfoRow icon={Phone} label="WhatsApp" value={user?.whatsapp || "—"} />
          <InfoRow icon={Briefcase} label="Tipe Pegawai" value={
            <Badge variant="outline" className={isHourly ? "border-amber-500 text-amber-700" : "border-blue-500 text-blue-700"}>
              {isHourly ? "PHL / Per Jam" : "Bulanan / Tetap"}
            </Badge>
          } />
          {!isHourly && <InfoRow icon={Wallet} label="Gaji Pokok" value={formatRupiah(user?.salary)} />}
          {!isHourly && <InfoRow icon={Wallet} label="Rate Lembur / Jam" value={formatRupiah(user?.overtime_rate)} />}
          {isHourly && <InfoRow icon={Wallet} label="Rate PHL / Jam" value={formatRupiah(user?.hourly_rate)} />}
          {!isHourly && <InfoRow icon={Percent} label="Max Kasbon" value={`${user?.max_cash_advance_pct || 0}%${user?.salary ? ` (${formatRupiah((user.salary * (user.max_cash_advance_pct || 0)) / 100)})` : ""}`} />}
        </div>
      </Card>

      {!isHourly && (
        <Link to="/pegawai/slip" className="block" data-testid="link-slip">
          <Card className="p-4 border border-zinc-200 shadow-none hover:border-primary transition-colors">
            <div className="flex items-center gap-3">
              <FileText className="w-5 h-5 text-primary" />
              <div className="flex-1">
                <p className="font-display font-semibold">Slip Gaji</p>
                <p className="text-xs text-zinc-500">Lihat & unduh slip gaji bulanan</p>
              </div>
              <ChevronRight className="w-4 h-4 text-zinc-400" />
            </div>
          </Card>
        </Link>
      )}

      {!isHourly && (
        <Link to="/pegawai/performa" className="block" data-testid="link-performa">
          <Card className="p-4 border border-zinc-200 shadow-none hover:border-primary transition-colors bg-gradient-to-br from-blue-50/50 to-purple-50/30">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-gradient-to-br from-blue-500 to-purple-600 flex items-center justify-center shrink-0">
                <Sparkles className="w-5 h-5 text-white" />
              </div>
              <div className="flex-1">
                <p className="font-display font-semibold">Performa Saya</p>
                <p className="text-xs text-zinc-500">Lihat skor & rincian performa bulanan</p>
              </div>
              <ChevronRight className="w-4 h-4 text-zinc-400" />
            </div>
          </Card>
        </Link>
      )}

      <PasswordChangeForm />
    </div>
  );
}

const InfoRow = ({ icon: Icon, label, value }) => (
  <div className="flex items-center gap-3 py-2.5">
    <Icon className="w-4 h-4 text-zinc-400 flex-shrink-0" />
    <div className="flex-1 min-w-0">
      <p className="text-xs text-zinc-500">{label}</p>
      <p className="text-sm font-medium text-zinc-900">{value}</p>
    </div>
  </div>
);

const PasswordChangeForm = () => {
  const [old, setOld] = useState("");
  const [neu, setNeu] = useState("");
  const [conf, setConf] = useState("");
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    if (neu.length < 6) return toast.error("Password baru min 6 karakter");
    if (neu !== conf) return toast.error("Konfirmasi tidak cocok");
    setBusy(true);
    try {
      await api.post("/auth/change-password", { old_password: old, new_password: neu });
      toast.success("Password berhasil diubah");
      setOld("");
      setNeu("");
      setConf("");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Gagal");
    } finally {
      setBusy(false);
    }
  };
  return (
    <Card className="p-4 border border-zinc-200 shadow-none">
      <p className="overline mb-3">Ubah Password</p>
      <form onSubmit={submit} className="space-y-3">
        <div><Label>Password Lama</Label><Input type="password" value={old} onChange={(e) => setOld(e.target.value)} required data-testid="input-old-pw" /></div>
        <div><Label>Password Baru</Label><Input type="password" value={neu} onChange={(e) => setNeu(e.target.value)} required data-testid="input-new-pw" /></div>
        <div><Label>Konfirmasi</Label><Input type="password" value={conf} onChange={(e) => setConf(e.target.value)} required data-testid="input-conf-pw" /></div>
        <Button type="submit" disabled={busy} className="w-full" data-testid="btn-change-pw">
          {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <><KeyRound className="w-4 h-4 mr-1.5" />Ubah Password</>}
        </Button>
      </form>
    </Card>
  );
};
